import { Lock } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';

import { Button } from '../components/ui/button';
import { Avatar, getAvatarInitials } from '../components/ui/avatar';
import { Input } from '../components/ui/input';
import { Field } from '../components/ui/form-field';
import { InlineAlert } from '../components/ui/inline-alert';
import { fieldLabelClassName } from '../components/ui/typography';
import {
    ADMIN_BADGE_BASE_CLASS,
    ADMIN_BADGE_POSITIVE_CLASS,
    ADMIN_BADGE_WARNING_CLASS,
} from '../components/admin/credit/adminStatusBadge';
import { cn } from '../lib/utils';
import type { ProfileUpdateError, ProfileUpdateRequest } from '../services/api';

type ProfileLike = {
    given_name?: string;
    family_name?: string;
    name?: string;
    email?: string;
    picture?: string;
    email_verified?: boolean;
    created_at: string;
    updated_at: string;
};

function RestrictedFieldHint({ restricted }: { restricted: boolean }) {
    if (!restricted) {
        return null;
    }

    return (
        <span className="inline-flex items-center gap-1 text-xs text-neutral-600">
            <Lock className="size-3 shrink-0 text-neutral-500" aria-hidden />
            (Managed by your identity provider)
        </span>
    );
}

function ProfileInputField({
    id,
    label,
    type,
    value,
    placeholder,
    restricted,
    onChange,
    onFieldInvalid,
}: {
    id: keyof ProfileUpdateRequest;
    label: string;
    type: string;
    value: string;
    placeholder: string;
    restricted: boolean;
    onChange: (field: keyof ProfileUpdateRequest, value: string) => void;
    onFieldInvalid: (element: HTMLInputElement) => void;
}) {
    const [error, setError] = useState<string>();
    const autoCompleteByField: Record<keyof ProfileUpdateRequest, string | undefined> = {
        given_name: 'given-name',
        family_name: 'family-name',
        name: 'name',
        email: 'email',
        picture: 'photo',
        user_metadata: undefined,
    };

    return (
        <Field
            label={label}
            hint={restricted ? <RestrictedFieldHint restricted /> : undefined}
            error={error}
        >
            <Input
                id={id}
                name={id}
                type={type}
                value={value}
                onChange={(event) => {
                    onChange(id, event.target.value);
                    if (event.currentTarget.validity.valid) setError(undefined);
                }}
                onBlur={(event) => {
                    const element = event.currentTarget;
                    setError(
                        element.validity.valid ? undefined : element.validationMessage || undefined,
                    );
                }}
                onInvalid={(event) => {
                    event.preventDefault();
                    setError(event.currentTarget.validationMessage || undefined);
                    onFieldInvalid(event.currentTarget);
                }}
                placeholder={placeholder}
                autoComplete={autoCompleteByField[id]}
                readOnly={restricted}
                title={restricted ? value : undefined}
                className="w-full [&[readonly]]:cursor-default [&[readonly]]:border-dashed [&[readonly]]:border-neutral-300 [&[readonly]]:bg-transparent [&[readonly]]:text-neutral-600"
            />
        </Field>
    );
}

function ProfileDisplayField({
    label,
    value,
    restricted,
    children,
}: {
    label: string;
    value?: string;
    restricted?: boolean;
    children?: React.ReactNode;
}) {
    return (
        <div>
            <dt className={cn('mb-1 flex flex-wrap items-center gap-x-2', fieldLabelClassName)}>
                {label}
                <RestrictedFieldHint restricted={Boolean(restricted)} />
            </dt>
            <dd className="break-words text-sm font-medium text-neutral-950">
                {children ?? value}
            </dd>
        </div>
    );
}

function ProfileUpdateErrorMessage({ error }: { error: Error }) {
    try {
        const errorData = JSON.parse(error.message) as ProfileUpdateError;
        if (errorData.restrictedFields) {
            return (
                <InlineAlert tone="error" title={errorData.error}>
                    {errorData.details ?? null}
                </InlineAlert>
            );
        }
    } catch {
        // Fall back to the generic error block below.
    }

    return (
        <InlineAlert tone="error" title="Couldn't save your profile">
            <p>Try again.</p>
            <p className="mt-1 break-words">{error.message}</p>
        </InlineAlert>
    );
}

export function ProfileEditForm({
    formData,
    restrictedFields,
    isPending,
    error,
    onInputChange,
    onSubmit,
    onCancel,
}: {
    formData: ProfileUpdateRequest;
    restrictedFields: string[];
    isPending: boolean;
    error: Error | null;
    onInputChange: (field: keyof ProfileUpdateRequest, value: string) => void;
    onSubmit: (event: React.FormEvent) => void;
    onCancel: () => void;
}) {
    const isFieldRestricted = (fieldName: string) => restrictedFields.includes(fieldName);
    const formRef = useRef<HTMLFormElement>(null);
    const submitButtonRef = useRef<HTMLButtonElement>(null);
    const invalidHandledRef = useRef(false);
    const wasPendingRef = useRef(false);

    // The submit button is disabled while saving, so a mouse-started save drops focus to <body>.
    // When the save ends with the form still on screen (an error), put focus back on Save.
    useEffect(() => {
        const active = document.activeElement;
        if (wasPendingRef.current && !isPending && (!active || active === document.body)) {
            submitButtonRef.current?.focus();
        }
        wasPendingRef.current = isPending;
    }, [isPending]);

    useEffect(() => {
        const initialControl =
            formRef.current?.querySelector<HTMLElement>('input:not([disabled]):not([readonly])') ??
            formRef.current?.querySelector<HTMLButtonElement>('button[type="button"]');
        initialControl?.focus();
    }, []);

    const handleFieldInvalid = (element: HTMLInputElement) => {
        if (invalidHandledRef.current) return;
        invalidHandledRef.current = true;
        element.focus();
        window.setTimeout(() => {
            invalidHandledRef.current = false;
        }, 0);
    };

    return (
        <form
            ref={formRef}
            onSubmit={onSubmit}
            onKeyDown={(event) => {
                if (event.key === 'Escape' && !isPending) {
                    event.preventDefault();
                    onCancel();
                }
            }}
            className="space-y-6"
        >
            {restrictedFields.length > 0 ? (
                <InlineAlert tone="info" title="Some fields are managed by your identity provider">
                    <p>
                        Fields marked “Managed by your identity provider” come from the account you
                        sign in with and can't be changed here.
                    </p>
                </InlineAlert>
            ) : null}

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <ProfileInputField
                    id="given_name"
                    label="First name"
                    type="text"
                    value={formData.given_name || ''}
                    placeholder="Enter your first name"
                    restricted={isFieldRestricted('given_name')}
                    onChange={onInputChange}
                    onFieldInvalid={handleFieldInvalid}
                />
                <ProfileInputField
                    id="family_name"
                    label="Last name"
                    type="text"
                    value={formData.family_name || ''}
                    placeholder="Enter your last name"
                    restricted={isFieldRestricted('family_name')}
                    onChange={onInputChange}
                    onFieldInvalid={handleFieldInvalid}
                />
            </div>

            <ProfileInputField
                id="name"
                label="Full name"
                type="text"
                value={formData.name || ''}
                placeholder="Enter your full name"
                restricted={isFieldRestricted('name')}
                onChange={onInputChange}
                onFieldInvalid={handleFieldInvalid}
            />
            <ProfileInputField
                id="email"
                label="Email address"
                type="email"
                value={formData.email || ''}
                placeholder="Enter your email address"
                restricted={isFieldRestricted('email')}
                onChange={onInputChange}
                onFieldInvalid={handleFieldInvalid}
            />
            <ProfileInputField
                id="picture"
                label="Profile picture URL"
                type="url"
                value={formData.picture || ''}
                placeholder="https://example.com/avatar.jpg"
                restricted={isFieldRestricted('picture')}
                onChange={onInputChange}
                onFieldInvalid={handleFieldInvalid}
            />

            {error ? <ProfileUpdateErrorMessage error={error} /> : null}

            <div className="flex items-center justify-end space-x-3 pt-6 border-t border-neutral-200">
                <Button type="button" variant="outline" onClick={onCancel} disabled={isPending}>
                    Cancel
                </Button>
                <Button
                    ref={submitButtonRef}
                    type="submit"
                    loading={isPending}
                    disabled={isPending}
                >
                    Save changes
                </Button>
            </div>
        </form>
    );
}

export function ProfileReadOnlyView({
    profile,
    restrictedFields,
}: {
    profile: ProfileLike;
    restrictedFields: string[];
}) {
    const isFieldRestricted = (fieldName: string) => restrictedFields.includes(fieldName);

    return (
        <div className="space-y-6">
            <div className="flex items-center space-x-4">
                <div className="flex-shrink-0">
                    <Avatar
                        src={profile.picture}
                        initials={getAvatarInitials(profile.name, profile.email)}
                    />
                </div>
                <div>
                    <h2 className="break-words text-lg font-semibold leading-tight text-neutral-950">
                        {profile.name || 'No name set'}
                    </h2>
                    <p className="text-sm text-neutral-600">{profile.email}</p>
                </div>
            </div>

            <dl className="grid grid-cols-1 gap-6 md:grid-cols-2">
                <ProfileDisplayField
                    label="First name"
                    value={profile.given_name || 'Not set'}
                    restricted={isFieldRestricted('given_name')}
                />
                <ProfileDisplayField
                    label="Last name"
                    value={profile.family_name || 'Not set'}
                    restricted={isFieldRestricted('family_name')}
                />
                <ProfileDisplayField
                    label="Full name"
                    value={profile.name || 'Not set'}
                    restricted={isFieldRestricted('name')}
                />
                <ProfileDisplayField
                    label="Email address"
                    value={profile.email || 'Not set'}
                    restricted={isFieldRestricted('email')}
                />
            </dl>

            <dl className="grid grid-cols-1 gap-6 border-t border-neutral-200 pt-6 md:grid-cols-3">
                <ProfileDisplayField label="Email verified">
                    <span
                        className={cn(
                            ADMIN_BADGE_BASE_CLASS,
                            profile.email_verified
                                ? ADMIN_BADGE_POSITIVE_CLASS
                                : ADMIN_BADGE_WARNING_CLASS,
                        )}
                    >
                        {profile.email_verified ? 'Verified' : 'Not verified'}
                    </span>
                </ProfileDisplayField>
                <ProfileDisplayField
                    label="Created"
                    value={new Date(profile.created_at).toLocaleDateString()}
                />
                <ProfileDisplayField
                    label="Last updated"
                    value={new Date(profile.updated_at).toLocaleDateString()}
                />
            </dl>
        </div>
    );
}
